import {
	Button,
	CircularProgress,
	Divider,
	Input,
	List,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { useUploadFiles } from "./hooks/useUploadFiles";
import { useEffect, useState } from "react";
import { ErrorModal, MESApiService } from "@dexteel/mesf-core";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
}));

type Props = {
	workOrderSelectedId: number | null;
	onHide: (shouldUpdate: boolean) => void;
	setError: (err: string) => void;
};

const MAX_FILE_SIZE = 100 * 1024 * 1024;

export const UploadAttachmentToWorkOrderDialog = ({
	workOrderSelectedId,
	onHide,
	setError,
}: Props) => {
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [attachmentsToAdd, setAttachmentsToAdd] = useState([]);
	const [selectedFilesToAdd, setSelectedFilesToAdd] = useState<File[]>([]);
	const [progressMap, setProgressMap] = useState<Record<string, number>>({});
	const [errorUploadingDocuments, setErrorUploadingDocuments] =
		useState<string>("");
	const { handleFileSelect, handleDragOver, handleDrop, setIsDragging } =
		useUploadFiles({
			selectedFilesToAdd,
			setSelectedFilesToAdd,
		});

	const classes = useStyles();

	const handleRemoveFile = (fileNameToRemove: string) => {
		const newArr = selectedFilesToAdd.filter(
			(file: any) => file.name !== fileNameToRemove,
		);
		setSelectedFilesToAdd(newArr);
	};

	const handleProgress = (event: any) => {
		if (
			event &&
			event.target &&
			event.target.files &&
			event.target.files.length > 0
		) {
			Array.from<File>(event.target.files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (e: ProgressEvent<FileReader>) => {
					if (e.lengthComputable) {
						const fileProgress = Math.round((e.loaded / e.total) * 100);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});
		}
	};

	const handleDropWithProgress = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		const files = e.dataTransfer.files;

		if (files.length > 0) {
			Array.from(files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (progressEvent: ProgressEvent<FileReader>) => {
					if (progressEvent.lengthComputable) {
						const fileProgress = Math.round(
							(progressEvent.loaded / progressEvent.total) * 100,
						);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});

			handleDrop(e);
			setIsDragging(false);
		}
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsSubmitLoading(true);

		if (!selectedFilesToAdd.length) {
			setIsSubmitLoading(false);
			return;
		}

		const oversizedFiles = selectedFilesToAdd.filter(
			(file) => file.size > MAX_FILE_SIZE,
		);
		if (oversizedFiles.length > 0) {
			setErrorUploadingDocuments(
				`These files exceed the accepted file size: ${oversizedFiles
					.map((f) => f.name)
					.join(", ")}`,
			);
			setIsSubmitLoading(false);
			return;
		}

		const api = new MESApiService();
		try {
			const res = await api.uploadFiles(selectedFilesToAdd, "Attachments");
			if (res) {
				setAttachmentsToAdd(res.result);
			} else {
				setErrorUploadingDocuments(res.message);
			}
		} catch (error) {
			setErrorUploadingDocuments("Something went wrong");
		} finally {
			setSelectedFilesToAdd([]);
			setIsSubmitLoading(false);
			setIsSubmitDisabled(false);
		}
	};

	useEffect(() => {
		if (attachmentsToAdd.length) {
			// Promise.allSettled(
			// 	attachmentsToAdd.map(async (att: WorkOrderAttachment) => {
			// 		const res = await addLinkWorkOrderAttachments(
			// 			workOrderSelectedId as number,
			// 			att.UploadFileId as number,
			// 			"",
			// 		);
			// 		if (!res.ok) {
			// 			setError(res.message);
			// 		}
			// 	}),
			// ).finally(() => {
			// 	onHide(true);
			// });
		} else {
			setIsSubmitDisabled(true);
		}
	}, [attachmentsToAdd]);

	useEffect(() => {
		if (selectedFilesToAdd.length < 1) {
			setIsSubmitDisabled(true);
		} else setIsSubmitDisabled(false);
	}, [selectedFilesToAdd]);

	useEffect(() => {
		setSelectedFilesToAdd([]);
	}, []);

	return (
		<>
			<form onSubmit={handleFormSubmit}>
				<Grid2 container style={{ padding: "15px 0 8px", overflow: "hidden" }}>
					<Grid2
						style={{
							alignItems: "center",
							marginTop: 0,
							padding: 0,
							margin: 0,
						}}
						spacing={2}
						size={{ md: 12, xs: 12 }}
					>
						<Grid2
							alignItems="center"
							style={{ padding: "8px 20px 8px" }}
							size={{ md: 12, xs: 12 }}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<div
									className={classes.formControl}
									onDragOver={(e) => {
										handleDragOver(e);
									}}
									onDragEnter={() => setIsDragging(true)}
									onDragLeave={() => setIsDragging(false)}
									onDragEnd={() => setIsDragging(false)}
									onDrop={(e) => {
										handleDrop(e);
										setIsDragging(false);
										handleDropWithProgress(e);
									}}
									style={{
										width: "100%",
										margin: "0",
										border: "2px dashed #3F51B5",
										borderRadius: "5px",
										padding: "20px",
										textAlign: "center",
										cursor: "pointer",
									}}
									onClick={() => document.getElementById("fileInput")?.click()}
								>
									<Typography>Drag & Drop Files here</Typography>
									<Typography>or</Typography>
									<Typography>Click Here</Typography>
									<Input
										type="file"
										onChange={(event: any) => {
											handleFileSelect(event);
											handleProgress(event);
										}}
										style={{ display: "none" }}
										id="fileInput"
										inputProps={{ multiple: true }}
									/>
								</div>
							</Grid2>
						</Grid2>

						{selectedFilesToAdd.length >= 1 && (
							<Grid2
								container
								style={{ marginTop: "10px", marginLeft: 20, padding: "0" }}
								size={{ md: 12, xs: 12 }}
							>
								<Grid2
									style={{
										justifyContent: "flex-start !important",
										alignItems: "flex-start",
									}}
									size={{ md: 12, xs: 12 }}
								>
									<Typography style={{ fontWeight: "bold" }}>
										Document Selected:
									</Typography>

									<List
										style={{
											height: "12vh",
											margin: "10px 45px 20px 0",
											padding: "10px",
											overflow: "auto",
											background: "#FCFCFC",
										}}
									>
										{selectedFilesToAdd.map((file: any) => (
											<>
												<div
													key={`file-${file.name}`}
													style={{
														textAlign: "left",
														display: "flex",
														alignItems: "center",
														padding: 0,
														margin: "5px 0",
													}}
												>
													<span
														style={{ marginRight: 5, cursor: "pointer" }}
														onClick={() => handleRemoveFile(file.name)}
													>
														<DeleteForeverIcon />
													</span>
													<p
														style={{
															margin: "5px 0",
															wordBreak: "break-all",
														}}
													>
														{file.name} ({(file.size / 1024).toFixed(2)} KB)
													</p>
												</div>
												{progressMap[file.name] > 0 && (
													<div
														style={{
															width: "100%",
															height: 20,
															backgroundColor: "#f2f2f2",
															borderRadius: 5,
														}}
													>
														<div
															style={{
																width: `${progressMap[file.name]}%`,
																height: "100%",
																backgroundColor: `rgba(63, 81, 181, ${
																	progressMap[file.name] / 100
																})`,
																borderRadius: "inherit",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																color: "white",
																fontWeight: "bold",
															}}
														>
															<span style={{ fontSize: "12px" }}>{`${
																progressMap[file.name]
															}%`}</span>
														</div>
													</div>
												)}
											</>
										))}
									</List>
								</Grid2>
							</Grid2>
						)}
					</Grid2>
					<Divider
						variant="fullWidth"
						style={{ margin: "10px 0", width: "150%" }}
					/>
					<Grid2
						justifyContent="flex-end"
						style={{ height: "35%", marginBottom: "auto", padding: "0 24px" }}
						size={{ md: 12, xs: 12 }}
					>
						<Grid2 container style={{ margin: 0, justifyContent: "flex-end" }}>
							<Grid2
								style={{ margin: 0, padding: "2px 0 8px 0" }}
								size={{ md: 5, xs: 6 }}
							>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={
										isSubmitLoading ||
										isSubmitDisabled ||
										selectedFilesToAdd.length == 0
									}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
					<ErrorModal
						error={errorUploadingDocuments}
						onHide={() => setErrorUploadingDocuments("")}
					/>
				</Grid2>
			</form>
		</>
	);
};
